import { makeStyles } from "@material-ui/core";

export const usePdvStyles = makeStyles((theme) => ({
  // ============================================
  // ROOT CONTAINER
  // ============================================
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
  },

  // ============================================
  // HEADER STYLES
  // ============================================
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 44,
    flexShrink: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    position: "relative",
    zIndex: 10,
  },

  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontWeight: 600,
    color: "inherit",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    "& .MuiChip-root": {
      backgroundColor: "rgba(255,255,255,0.2)",
      color: "inherit",
      borderColor: "rgba(255,255,255,0.5)",
    },
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
    "& .MuiIconButton-root": {
      color: "inherit",
    },
  },

  // ============================================
  // CART BUTTON (FLOATING)
  // ============================================
  cartButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "inherit",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid rgba(255,255,255,0.3)",
    outline: "none",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.3)",
    },
    "&:focus": {
      boxShadow: `0 0 0 2px rgba(255,255,255,0.5)`,
    },
  },

  cartButtonEmpty: {
    backgroundColor: "rgba(255,255,255,0.1)",
    borderColor: "rgba(255,255,255,0.2)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    },
  },

  cartButtonWithItems: {
    animation: "$pulse 2s infinite",
  },

  cartButtonBadge: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.875rem",
    fontWeight: 600,
  },

  cartButtonTotal: {
    fontSize: "0.875rem",
    fontWeight: 700,
    marginLeft: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    borderLeft: `1px solid ${theme.palette.primary.light}`,
  },

  "@keyframes pulse": {
    "0%": {
      boxShadow: `0 0 0 0 ${theme.palette.primary.main}40`,
    },
    "70%": {
      boxShadow: `0 0 0 8px ${theme.palette.primary.main}00`,
    },
    "100%": {
      boxShadow: `0 0 0 0 ${theme.palette.primary.main}00`,
    },
  },

  // ============================================
  // CUSTOMER SUMMARY
  // ============================================
  customerSummary: {
    padding: theme.spacing(0.75, 1.25),
    margin: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
    flexShrink: 0,
    position: "relative",
    zIndex: 1,
    overflow: "hidden",
  },

  customerSummaryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },

  customerName: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    "& > span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },

  customerDetails: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(0.25, 0.5),
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },

  customerDetailItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "& svg": {
      fontSize: "0.875rem",
      color: theme.palette.text.disabled,
    },
  },

  customerStats: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
    borderTop: `1px dashed ${theme.palette.divider}`,
    fontSize: "0.7rem",
  },

  customerStatItem: {
    display: "flex",
    flexDirection: "column",
    "& .label": {
      color: theme.palette.text.disabled,
      fontSize: "0.65rem",
      textTransform: "uppercase",
    },
    "& .value": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },

  customerNotIdentified: {
    color: theme.palette.warning.main,
    fontStyle: "italic",
  },

  // ============================================
  // SEARCH BAR
  // ============================================
  searchContainer: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },

  searchInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius * 2,
      backgroundColor: theme.palette.background.paper,
      transition: "all 0.2s ease",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  },

  searchInputActive: {
    "& .MuiOutlinedInput-root": {
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}30`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },

  searchShortcutHint: {
    color: theme.palette.text.disabled,
    fontSize: "0.75rem",
    marginLeft: theme.spacing(1),
  },

  // ============================================
  // PRODUCT LIST
  // ============================================
  productListContainer: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  productList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.divider,
      borderRadius: 3,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },

  productListActive: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
    borderRadius: theme.shape.borderRadius,
  },

  // ============================================
  // PRODUCT ITEM
  // ============================================
  productItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.light,
    },
    "&:last-child": {
      marginBottom: 0,
    },
  },

  productItemSelected: {
    backgroundColor: theme.palette.primary.main + "15",
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
  },

  productItemInfo: {
    flex: "1 1 auto",
    minWidth: 0,
    marginRight: theme.spacing(1),
  },

  productName: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.25),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  productDetails: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  productActivePrinciple: {
    fontSize: "0.75rem",
    color: theme.palette.info.main,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: theme.spacing(0.25),
  },

  productBarcode: {
    color: theme.palette.text.secondary,
  },

  productStock: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
  },

  productStockLow: {
    color: theme.palette.warning.main,
  },

  productStockOut: {
    color: theme.palette.error.main,
  },

  productPriceSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },

  productPriceControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },

  productPrice: {
    fontSize: "1rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  productQuantityControls: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.25),
  },

  productQtyButton: {
    minWidth: 24,
    width: 24,
    height: 24,
    padding: 0,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
    },
  },

  productQtyDisplay: {
    minWidth: 20,
    textAlign: "center",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  productAddButton: {
    minWidth: 30,
    width: 30,
    height: 30,
    padding: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },

  productSendButton: {
    minWidth: 28,
    width: 28,
    height: 28,
    padding: 0,
    color: theme.palette.success.main,
    "&:hover": {
      backgroundColor: theme.palette.success.main + "15",
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
    },
  },

  // ============================================
  // EMPTY STATE
  // ============================================
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },

  emptyStateIcon: {
    fontSize: 24,
    color: theme.palette.divider,
    marginBottom: theme.spacing(1),
  },

  emptyStateText: {
    fontSize: "0.875rem",
  },

  // ============================================
  // SUGGESTIONS PANEL
  // ============================================
  suggestionsPanel: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[900] : "#f5f7fa",
    maxHeight: 160,
    minHeight: 0,
    overflowY: "auto",
    flexShrink: 0,
    transition: "border-color 0.2s ease",
  },

  suggestionsPanelActive: {
    borderTop: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `inset 0 3px 5px -3px ${theme.palette.primary.main}40`,
  },

  suggestionsPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: 0,
    backgroundColor: "inherit",
    zIndex: 1,
  },

  suggestionsPanelTitle: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },

  suggestionsPanelLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },

  suggestionSection: {
    padding: theme.spacing(1),
    "&:not(:last-child)": {
      borderBottom: `1px dashed ${theme.palette.divider}`,
    },
  },

  suggestionSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },

  suggestionSectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1rem",
  },

  suggestionSectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontSize: "0.65rem",
    letterSpacing: "0.5px",
  },

  activePrincipleChip: {
    height: 18,
    fontSize: "0.65rem",
    marginLeft: theme.spacing(0.5),
  },

  suggestionsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },

  suggestionItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.action.hover,
    },
  },

  suggestionItemSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + "15",
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "20",
    },
  },

  suggestionItemInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(1),
  },

  suggestionItemName: {
    fontSize: "0.8rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.text.primary,
  },

  suggestionItemDetails: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },

  suggestionItemPrice: {
    fontSize: "0.85rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  suggestionItemStock: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
  },

  suggestionItemActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
  },

  suggestionSwapButton: {
    color: theme.palette.info.main,
  },

  suggestionPriceDiff: {
    height: 18,
    fontSize: "0.65rem",
    "& .MuiChip-icon": {
      fontSize: "0.75rem",
    },
  },

  suggestionPriceCheaper: {
    backgroundColor: theme.palette.success.main + "20",
    color: theme.palette.success.dark,
    "& .MuiChip-icon": {
      color: theme.palette.success.dark,
    },
  },

  suggestionPriceExpensive: {
    backgroundColor: theme.palette.error.main + "20",
    color: theme.palette.error.dark,
    "& .MuiChip-icon": {
      color: theme.palette.error.dark,
    },
  },

  suggestionsEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },

  suggestionsTabs: {
    minHeight: 32,
    "& .MuiTab-root": {
      minHeight: 32,
      minWidth: "auto",
      padding: theme.spacing(0.5, 1.5),
      fontSize: "0.75rem",
    },
  },

  suggestionsContent: {
    padding: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
  },

  suggestionCard: {
    minWidth: 140,
    maxWidth: 140,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[2],
    },
  },

  suggestionCardName: {
    fontSize: "0.75rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: theme.spacing(0.5),
  },

  suggestionCardPrice: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  suggestionCardPriceDiff: {
    fontSize: "0.65rem",
    marginLeft: theme.spacing(0.5),
  },

  suggestionCardPriceCheaper: {
    color: theme.palette.success.main,
  },

  suggestionCardPriceMore: {
    color: theme.palette.error.main,
  },

  // ============================================
  // CART DRAWER
  // ============================================
  cartDrawer: {
    "& .MuiDrawer-root": {
      position: "absolute",
    },
    "& .MuiDrawer-modal": {
      position: "absolute",
    },
    "& .MuiBackdrop-root": {
      position: "absolute",
    },
    "& .MuiDrawer-paper": {
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      right: 0,
      borderRadius: 0,
      boxShadow: "none",
    },
  },

  cartDrawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  cartDrawerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "1.125rem",
    fontWeight: 600,
  },

  cartDrawerContent: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
  },

  cartDrawerFooter: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },

  // ============================================
  // CART ITEM
  // ============================================
  cartItem: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.15s ease",
    "&:last-child": {
      marginBottom: 0,
    },
  },

  cartItemSelected: {
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
    backgroundColor: theme.palette.secondary.main + "10",
  },

  cartItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
  },

  cartItemName: {
    fontSize: "0.875rem",
    fontWeight: 600,
    flex: 1,
    marginRight: theme.spacing(1),
  },

  cartItemRemove: {
    padding: 4,
    "& svg": {
      fontSize: "1rem",
    },
  },

  cartItemBody: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  cartItemQuantity: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },

  cartItemQuantityButton: {
    minWidth: 28,
    width: 28,
    height: 28,
    padding: 0,
  },

  cartItemQuantityInput: {
    width: 40,
    "& input": {
      textAlign: "center",
      padding: theme.spacing(0.5),
      fontSize: "0.875rem",
    },
  },

  cartItemPricing: {
    textAlign: "right",
  },

  cartItemOriginalPrice: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    textDecoration: "line-through",
  },

  cartItemUnitPrice: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  cartItemTotal: {
    fontSize: "1rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  cartItemDiscount: {
    fontSize: "0.65rem",
    color: theme.palette.success.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
  },

  cartItemActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px dashed ${theme.palette.divider}`,
  },

  // ============================================
  // CART SUMMARY
  // ============================================
  cartSummary: {
    marginBottom: theme.spacing(2),
  },

  cartSummaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    fontSize: "0.875rem",
  },

  cartSummaryLabel: {
    color: theme.palette.text.secondary,
  },

  cartSummaryValue: {
    fontWeight: 500,
  },

  cartSummaryDiscount: {
    color: theme.palette.success.main,
  },

  cartSummaryTotal: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.divider}`,
    fontSize: "1.25rem",
    fontWeight: 700,
  },

  cartSummaryTotalValue: {
    color: theme.palette.primary.main,
  },

  // ============================================
  // PAYMENT SELECTOR
  // ============================================
  paymentSelector: {
    marginBottom: theme.spacing(2),
  },

  paymentSelectorLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    fontWeight: 600,
  },

  paymentOptions: {
    display: "flex",
    gap: theme.spacing(1),
  },

  paymentOption: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.15s ease",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },

  paymentOptionSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    backgroundColor: theme.palette.primary.main + "15",
  },

  paymentOptionIcon: {
    fontSize: "1.25rem",
    marginBottom: theme.spacing(0.25),
  },

  paymentOptionLabel: {
    fontSize: "0.65rem",
    fontWeight: 500,
  },

  // ============================================
  // CART ACTIONS
  // ============================================
  cartActions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },

  cartActionButton: {
    justifyContent: "flex-start",
    padding: theme.spacing(1.5, 2),
  },

  cartActionPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  cartActionSecondary: {
    border: `1px solid ${theme.palette.divider}`,
  },

  cartActionDanger: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.error.main + "10",
    },
  },

  // ============================================
  // SECTION ACTIVE INDICATOR
  // ============================================
  sectionActive: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 3,
      height: "80%",
      backgroundColor: theme.palette.primary.main,
      borderRadius: "0 2px 2px 0",
    },
  },

  // ============================================
  // LOADING STATES
  // ============================================
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },

  skeleton: {
    borderRadius: theme.shape.borderRadius,
  },

  // ============================================
  // KEYBOARD SHORTCUT HINTS
  // ============================================
  shortcutHint: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 20,
    height: 20,
    padding: theme.spacing(0, 0.5),
    borderRadius: 4,
    backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[700] : theme.palette.grey[200],
    color: theme.palette.text.secondary,
    fontSize: "0.65rem",
    fontWeight: 600,
    fontFamily: "monospace",
  },

  // ============================================
  // QUOTE INDICATOR
  // ============================================
  quoteIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.info.main + "20",
    color: theme.palette.info.main,
    fontSize: "0.75rem",
    fontWeight: 500,
  },

  
  // ============================================
  // FLOATING CART BUTTON
  // ============================================
  floatingCartButton: {
    position: 'absolute',
    bottom: 68, // Alinhado com botao "Confirmar Pedido" do drawer (12 padding + 40 btn secundario + 8 margin + 8 ajuste)
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 3),
    minHeight: 48,
    borderRadius: 24,
    backgroundColor: theme.palette.grey[500],
    color: '#fff',
    cursor: 'pointer',
    boxShadow: theme.shadows[6],
    border: 'none',
    outline: 'none',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    '&:hover': {
      backgroundColor: theme.palette.grey[600],
      transform: 'translateX(-50%) scale(1.05)',
      boxShadow: theme.shadows[10],
    },
  },

  floatingCartButtonWithItems: {
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(1.5, 3),
    animation: '$floatPulseStrong 1s infinite',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  floatingCartButtonIcon: {
    fontSize: '24px !important',
  },

  floatingCartButtonBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    fontWeight: 700,
    fontSize: '0.875rem',
  },

  floatingCartButtonTotal: {
    fontWeight: 700,
    fontSize: '0.95rem',
    marginLeft: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    borderLeft: '1px solid rgba(255,255,255,0.3)',
  },

  '@keyframes floatPulseStrong': {
    '0%': {
      boxShadow: '0 0 0 0 ' + theme.palette.primary.main + 'cc',
      transform: 'translateX(-50%) scale(1)',
    },
    '50%': {
      boxShadow: '0 0 0 15px ' + theme.palette.primary.main + '00',
      transform: 'translateX(-50%) scale(1.08)',
    },
    '100%': {
      boxShadow: '0 0 0 0 ' + theme.palette.primary.main + '00',
      transform: 'translateX(-50%) scale(1)',
    },
  },

// ============================================
  // PENDING QUOTE NOTIFICATION
  // ============================================
  pendingQuoteNotification: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1, 1.5),
    cursor: "pointer",
    animation: "$pendingQuotePulse 2s infinite",
    boxShadow: theme.shadows[4],
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
      transform: "scale(1.02)",
      boxShadow: theme.shadows[8],
    },
  },

  pendingQuoteIcon: {
    fontSize: "1.5rem",
    animation: "$bellRing 1s ease-in-out infinite",
  },

  pendingQuoteContent: {
    flex: 1,
    minWidth: 0,
  },

  pendingQuoteTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    lineHeight: 1.2,
  },

  pendingQuoteSubtitle: {
    fontSize: "0.75rem",
    opacity: 0.9,
  },

  pendingQuoteAction: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.75rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  pendingQuoteDismiss: {
    padding: theme.spacing(0.5),
    color: "inherit",
    opacity: 0.8,
    "&:hover": {
      opacity: 1,
      backgroundColor: "rgba(255,255,255,0.2)",
    },
  },

  "@keyframes pendingQuotePulse": {
    "0%": {
      boxShadow: `0 0 0 0 ${theme.palette.warning.main}80`,
    },
    "50%": {
      boxShadow: `0 0 0 8px ${theme.palette.warning.main}00`,
    },
    "100%": {
      boxShadow: `0 0 0 0 ${theme.palette.warning.main}00`,
    },
  },

  "@keyframes bellRing": {
    "0%": { transform: "rotate(0)" },
    "10%": { transform: "rotate(15deg)" },
    "20%": { transform: "rotate(-15deg)" },
    "30%": { transform: "rotate(10deg)" },
    "40%": { transform: "rotate(-10deg)" },
    "50%": { transform: "rotate(5deg)" },
    "60%": { transform: "rotate(-5deg)" },
    "70%": { transform: "rotate(0)" },
    "100%": { transform: "rotate(0)" },
  },

  // ============================================
  // RESPONSIVE ADJUSTMENTS
  // ============================================
  "@media (max-width: 600px)": {
    cartDrawer: {
      "& .MuiDrawer-paper": {
        width: "100%",
      },
    },
    customerDetails: {
      gridTemplateColumns: "1fr",
    },
    paymentOptions: {
      flexWrap: "wrap",
    },
    paymentOption: {
      flex: "1 1 45%",
    },
  },
}));

export default usePdvStyles;
